import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {clearSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {
    useGetActiveSearchTemplate,
    useGetSearchTemplates,
    useSetActiveSearchTemplate,
} from 'src/components/edit-columns/hooks';
import {usePlatformColumn, usePlatformSorting} from 'src/components/user-platform-column/use-platform-column';
import {isProductColumn} from 'src/components/user-product-columns/product-columns';
import {ProductColumnsProvider} from 'src/components/user-product-columns/product-columns-context';
import {selectUserShortInfo} from 'src/modules/main/services/user-info-slice';
import {runUserIdsSearch} from 'src/openapi/admin/apis/UserSearchApi';
import {SearchType} from 'src/openapi/admin/models/SearchType';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {UserSortField as UserSortFields} from 'src/openapi/admin/models/UserSortField';
import {useAppDispatch} from 'src/store/hooks';

import Loading from '@pb/kit-component-loading';

import {COLUMNS, SELECTION_KEY, SORTING_ERROR, TOTAL_LIMIT_COLUMNS} from './constants';
import {limitDetailsActions} from './slices/limit-details.slice';
import {userSearchResultAccountsActions} from './slices/user-search-result-accounts.slice';
import {userSearchResultAuditActions} from './slices/user-search-result-audit.slice';
import {selectSelectedColumns, updateSelectedUserSearchColumns} from './slices/user-search-result-columns.slice';
import {userSearchResultCrmIdsActions} from './slices/user-search-result-crm-ids.slice';
import {userSearchResultGroupsActions} from './slices/user-search-result-groups.slice';
import {userSearchResultLastActiveActions} from './slices/user-search-result-last-active.slice';
import {userSearchResultLcdSubscriptionsActions} from './slices/user-search-result-lcd-subscriptions.slice';
import {userSearchResultLimitsActions} from './slices/user-search-result-limits.slice';
import {userSearchResultNewslettersActions} from './slices/user-search-result-newsletters.slice';
import {userSearchResultSavedSearchesActions} from './slices/user-search-result-saved-searches.slice';
import {userSearchResultStateActions, userSearchResultStateSelector} from './slices/user-search-result-state.slice';
import {
    userSearchResultActions,
    userSearchResultDataSelector,
    userSearchResultResultSelector,
} from './slices/user-search-result.slice';
import {getSearchFilteringCriteria, mapColumnIdToUserSortField} from './utils';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {UserSearchCriteria} from 'src/openapi/admin/models/UserSearchCriteria';
import type {UserSearchFiltering} from 'src/openapi/admin/models/UserSearchFiltering';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

export type ChildrenProps = {
    adminId: number,
    isLoading: boolean,
    totalCount: number,
    isActiveTemplateLoading: boolean,
    getAllEntityIds: () => Promise<{
        type: string,
        payload: {
            data: string[] | number[],
            success: true,
            error: false,
        } | {
            data: ApiError,
            success: false,
            error: true,
        },
    }>,
    activeSearchTemplate: UserSearchTemplate,
    loadActiveSearchTemplate: () => Promise<void>,
    setActiveSearchTemplate: (actionProps: UserSearchTemplate) => Promise<string | null>,
    templates: UserSearchTemplate[],
    loadTemplates: () => void,
    refreshResults: () => void,
};

type Props = {
    place: string,
    children: (props: ChildrenProps) => React.ReactElement,
    noCriteriaView?: React.ReactElement,
};

export const UserSearchResultController = ({children, noCriteriaView, place}: Props) => {
    const {getCurrentSorting} = usePlatformColumn();
    const [productRefreshVersion, setProductRefreshVersion] = useState(0);
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const userShortInfo = useSelector(selectUserShortInfo);
    const selectedColumns = useSelector(selectSelectedColumns);

    const result = useSelector(userSearchResultResultSelector);
    const {loadingError} = useSelector(userSearchResultDataSelector);
    const cacheKeys = useSelector(userSearchResultStateSelector);

    const adminId = useMemo(() => userShortInfo?.userId || 0, [userShortInfo]);

    const refreshResults = useCallback((columnsOverride?: string[]) => {
        const cols = Array.isArray(columnsOverride) ? columnsOverride : selectedColumns;

        setProductRefreshVersion(version => version + 1);
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(limitDetailsActions.clearData());
        dispatch(userSearchResultStateActions.setFilters({}));
        dispatch(userSearchResultActions.requireRefresh());

        if (cols.includes(COLUMNS.ACCOUNT_ID)) {
            dispatch(userSearchResultAccountsActions.requireRefresh());
        }

        if (cols.includes(COLUMNS.GROUP_ID)) {
            dispatch(userSearchResultGroupsActions.requireRefresh());
        }

        if (cols.includes(COLUMNS.NEWSLETTERS)) {
            dispatch(userSearchResultNewslettersActions.requireRefresh());
        }

        if ([
            COLUMNS.LAST_PLATFORM_ACTIVITY,
            COLUMNS.LAST_MOBILE_ACTIVITY,
        ].some(column => cols.includes(column))) {
            dispatch(userSearchResultLastActiveActions.requireRefresh());
        }

        if (cols.includes(COLUMNS.SAVED_SEARCHES)) {
            dispatch(userSearchResultSavedSearchesActions.requireRefresh());
        }

        if ([
            COLUMNS.CREATOR,
            COLUMNS.CREATED_DATE,
            COLUMNS.LAST_MODIFIER,
            COLUMNS.LAST_MODIFIED_DATE,
            COLUMNS.LAST_MODIFICATION_REASON,
        ].some(column => cols.includes(column))) {
            dispatch(userSearchResultAuditActions.requireRefresh());
        }

        if ([
            COLUMNS.SALESFORCE_ID,
            COLUMNS.MS_DYNAMICS_ID,
            COLUMNS.DEAL_CLOUD_ID,
            COLUMNS.HUBSPOT_ID,
        ].some(column => cols.includes(column))) {
            dispatch(userSearchResultCrmIdsActions.requireRefresh());
        }

        if (TOTAL_LIMIT_COLUMNS.some(column => cols.includes(column))) {
            dispatch(userSearchResultLimitsActions.requireRefresh());
        }

        if ([
            COLUMNS.LCD_SUBSCRIPTION_NEWS,
            COLUMNS.LCD_SUBSCRIPTION_RESEARCH,
        ].some(column => cols.includes(column))) {
            dispatch(userSearchResultLcdSubscriptionsActions.requireRefresh());
        }
    }, [selectedColumns, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(limitDetailsActions.clearData());
            dispatch(userSearchResultActions.clearData());
            dispatch(userSearchResultStateActions.clearData());

            if (selectedColumns.includes(COLUMNS.ACCOUNT_ID)) {
                dispatch(userSearchResultAccountsActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.GROUP_ID)) {
                dispatch(userSearchResultGroupsActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.NEWSLETTERS)) {
                dispatch(userSearchResultNewslettersActions.clearData());
            }

            if ([
                COLUMNS.LAST_PLATFORM_ACTIVITY,
                COLUMNS.LAST_MOBILE_ACTIVITY,
            ].some(column => selectedColumns.includes(column))) {
                dispatch(userSearchResultLastActiveActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.SAVED_SEARCHES)) {
                dispatch(userSearchResultSavedSearchesActions.clearData());
            }

            if ([
                COLUMNS.CREATOR,
                COLUMNS.CREATED_DATE,
                COLUMNS.LAST_MODIFIER,
                COLUMNS.LAST_MODIFIED_DATE,
                COLUMNS.LAST_MODIFICATION_REASON,
            ].some(column => selectedColumns.includes(column))) {
                dispatch(userSearchResultAuditActions.clearData());
            }

            if ([
                COLUMNS.SALESFORCE_ID,
                COLUMNS.MS_DYNAMICS_ID,
                COLUMNS.DEAL_CLOUD_ID,
                COLUMNS.HUBSPOT_ID,
            ].some(column => selectedColumns.includes(column))) {
                dispatch(userSearchResultCrmIdsActions.clearData());
            }

            if (TOTAL_LIMIT_COLUMNS.some(column => selectedColumns.includes(column))) {
                dispatch(userSearchResultLimitsActions.clearData());
            }

            if ([
                COLUMNS.LCD_SUBSCRIPTION_NEWS,
                COLUMNS.LCD_SUBSCRIPTION_RESEARCH,
            ].some(column => selectedColumns.includes(column))) {
                dispatch(userSearchResultLcdSubscriptionsActions.clearData());
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const {
        isLoading: isTemplatesLoading,
        data: templates,
        loadData: loadTemplates,
    } = useGetSearchTemplates({userId: adminId});


    const {
        isLoading: isActiveTemplateLoading,
        data: activeSearchTemplate,
        loadData: loadActiveSearchTemplate,
    } = useGetActiveSearchTemplate({
        adminId,
        searchType: SearchType.USER,
        onLoad: (columns, field, direction) => {
            const sorting = getCurrentSorting(field, direction);
            const updatedColumns = columns.map(col => isProductColumn(col) ? col : COLUMNS[col]).filter(Boolean);
            dispatch(updateSelectedUserSearchColumns(updatedColumns));
            dispatch(
                userSearchResultStateActions.setSortCriteria({
                    direction: sorting.direction,
                    field: mapColumnIdToUserSortField(COLUMNS[sorting.column]),
                }),
            );
            dispatch(userSearchResultStateActions.setFilters({}));
            refreshResults(updatedColumns);
        },
    });

    usePlatformSorting(activeSearchTemplate, (column, direction) => {
        dispatch(userSearchResultStateActions.setSortCriteria({
            direction, field: mapColumnIdToUserSortField(COLUMNS[column]),
        }));
    });

    const {event: setActiveSearchTemplate} = useSetActiveSearchTemplate({
        userId: adminId,
        onSuccess: loadActiveSearchTemplate,
        place,
    });

    const getAllEntityIds = useCallback(() => apiDispatch(runUserIdsSearch({
        userIdsSearchRequest: {
            ...getSearchFilteringCriteria(
                cacheKeys.searchCriteria as UserSearchCriteria,
                cacheKeys.filters as UserSearchFiltering,
            ),
            userSortCriteria: {
                columnId: cacheKeys.sortCriteria.field,
                direction: cacheKeys.sortCriteria.direction,
            },
        },
    })), [cacheKeys, apiDispatch]);


    useEffect(() => {
        if (loadingError?.message === SORTING_ERROR) {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(userSearchResultStateActions.setSortCriteria({
                direction: SortDirections.ASC,
                field: UserSortFields.EMAIL,
            }));
        }
    }, [loadingError, dispatch]);

    const isLoading = useMemo(() => (
        isTemplatesLoading || isActiveTemplateLoading || !result
    ), [isTemplatesLoading, isActiveTemplateLoading, result]);

    if (!adminId || activeSearchTemplate.templateId === -1) {
        return <Loading fast={true}/>;
    }

    if (!Object.keys(cacheKeys.searchCriteria).length) {
        return noCriteriaView || <Loading fast={true}/>;
    }

    return (
        <ProductColumnsProvider refreshVersion={productRefreshVersion}>
            {children({
                adminId,
                isLoading,
                totalCount: result?.totalCount || 0,
                isActiveTemplateLoading,
                activeSearchTemplate,
                getAllEntityIds,
                loadActiveSearchTemplate,
                setActiveSearchTemplate,
                templates,
                loadTemplates,
                refreshResults,
            })}
        </ProductColumnsProvider>
    );
};
